import React from 'react';
import { LibraryListLoading } from '~/components/list/library-list-loading';
import { flushSync } from 'react-dom';
import { Await, useLoaderData, useRevalidator } from 'react-router-dom';
import { Button } from '@oysterjs/ui/button';
import { PageTitle } from '@oysterjs/ui/page';
import { Dialog, DialogTitle, DialogDescription, DialogActions } from '@oysterjs/ui/dialog';
import { type CustomPresetFieldsFragment } from '~/api/graphql';
import {
  MailboxCreationOptionsProvider,
  TriggerEmailCreatePage
} from '~/pages/workflows/trigger-email-create';
import {
  CREATION_EVENTS,
  CreationEventContext,
  CreationNameField,
  CreationEventLoadState,
  type CreationEvent,
  type CreationEventGuard
} from '~/components/triggers/email-builder/creation-event';
import { TriggerEditForm, TriggersProGate } from '~/pages/workflows/trigger-edit';
import { useTriggersEntitlement } from '~/pages/workflows/use-triggers-entitlement';
import { FEATURE_FLAGS, useFlag } from '~/components/feature-flags';

// Each event has its own editor, so a switch replaces the WHEN/IF/THEN cards. Where the
// browser supports it, morph the old cards into the new ones (creation-editor.css)
// instead of swapping them in one frame.
const switchEditor = (apply: () => void) => {
  if (
    typeof document.startViewTransition !== 'function' ||
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  ) {
    apply();
    return;
  }
  const root = document.documentElement;
  const done = () => root.classList.remove('email-creation-switching');
  root.classList.add('email-creation-switching');
  const transition = document.startViewTransition(() => flushSync(apply));
  transition.ready.catch(() => {});
  transition.finished.then(done, done);
};

export const TriggerCreationPage = () => (
  <MailboxCreationOptionsProvider>
    <TriggerCreationEditor />
  </MailboxCreationOptionsProvider>
);

const TriggerCreationEditor = () => {
  const entitled = useTriggersEntitlement();
  const loader = useLoaderData() as
    | { data: Promise<{ customPresets: CustomPresetFieldsFragment[] }> }
    | undefined;
  const revalidator = useRevalidator();
  const hideMailbox = useFlag(FEATURE_FLAGS.HIDE_MAILBOX_TRIGGER_EVENT);
  const [selectedEvent, setEvent] = React.useState<CreationEvent>('mailbox');
  const event = hideMailbox && selectedEvent === 'mailbox' ? 'alias' : selectedEvent;
  const [name, setName] = React.useState('');
  const [nameError, setNameError] = React.useState<string>();
  const [pending, setPending] = React.useState<CreationEvent | null>(null);
  const [guard, setGuard] = React.useState<CreationEventGuard>({ dirty: false, locked: false });
  const refocus = React.useRef(false);
  const onChange = React.useCallback(
    (next: CreationEvent) => {
      if (guard.locked || event === next) return;
      if (guard.dirty) setPending(next);
      else
        switchEditor(() => {
          refocus.current = true;
          setEvent(next);
        });
    },
    [event, guard]
  );
  const context = React.useMemo(
    () => ({
      value: event,
      onChange,
      guard,
      setGuard,
      refocus,
      name,
      setName,
      nameError,
      setNameError
    }),
    [event, onChange, guard, name, nameError]
  );
  const selected = CREATION_EVENTS.find((choice) => choice.value === event)!;
  const loading = (
    <CreationEventLoadState>
      <LibraryListLoading embedded title="Loading agency presets…" />
    </CreationEventLoadState>
  );
  const failed = (
    <CreationEventLoadState>
      <div role="alert">
        <p>Unable to load agency presets. Your draft has not been saved.</p>
        <Button
          color="white"
          type="button"
          disabled={revalidator.state === 'loading'}
          onClick={() => revalidator.revalidate()}
        >
          Retry loading presets
        </Button>
      </div>
    </CreationEventLoadState>
  );
  return (
    <CreationEventContext.Provider value={context}>
      <PageTitle
        eyebrow="Triggers"
        eyebrowHref="/app/workflows/triggers"
        title="Create trigger"
        description="Configure an event and the workspace it will launch."
      >
        <div className="space-y-6">
          <CreationNameField />
          {event === 'mailbox' ? (
            <TriggerEmailCreatePage />
          ) : !entitled ? (
            <CreationEventLoadState>
              <TriggersProGate />
            </CreationEventLoadState>
          ) : (
            <React.Suspense fallback={loading}>
              {loader?.data ? (
                <Await resolve={loader.data} errorElement={failed}>
                  {(data) => (
                    <TriggerEditForm
                      key={event}
                      existing={null}
                      customPresets={data.customPresets ?? []}
                      creationEvent={selected.eventType}
                    />
                  )}
                </Await>
              ) : (
                failed
              )}
            </React.Suspense>
          )}
        </div>
        <Dialog open={pending !== null} onClose={() => setPending(null)}>
          <DialogTitle>Start a different event draft?</DialogTitle>
          <DialogDescription>
            Changing the event starts a new draft. The name stays, but your current settings and
            instructions will not carry over. Nothing has been saved.
          </DialogDescription>
          <DialogActions>
            <Button color="white" type="button" onClick={() => setPending(null)}>
              Keep editing
            </Button>
            <Button
              color="sky"
              type="button"
              onClick={() => {
                if (!pending || guard.locked) {
                  setPending(null);
                  return;
                }
                switchEditor(() => {
                  setGuard({ dirty: false, locked: false });
                  refocus.current = true;
                  setEvent(pending);
                  setPending(null);
                });
              }}
            >
              Change event
            </Button>
          </DialogActions>
        </Dialog>
      </PageTitle>
    </CreationEventContext.Provider>
  );
};
